import { buildCreateBundleValidationData, CREATE_BUNDLE_STEP_KEYS } from '../createBundleConsts';
import NotificationProvider from 'components/NotificationProvider';
import { LOT_OPTIONS } from '../../CreateActivityAndPricing/ActivityItemPricing/ActivityItemPricingConsts';
import isEqual from 'lodash/isEqual';
import { normalizePricing, normalizeRules } from '../../utils/lotOptionsMapper';

export const isBundleStepModified = (originalSteps, currentSteps, stepIndex) => {
    if (!originalSteps) return false;

    switch (stepIndex) {
        case 0: {
            const orig = originalSteps?.[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS];
            const curr = currentSteps?.[CREATE_BUNDLE_STEP_KEYS.BUNDLE_DETAILS];
            if (!orig || !curr) return false;
            const keys = ['name', 'description', 'activityItemIds', 'paramTypes'];
            return keys.some(key => !isEqual(orig?.[key], curr?.[key]));
        }
        case 1:
            return !isEqual(
                normalizePricing(originalSteps[CREATE_BUNDLE_STEP_KEYS.PRICING]?.pricingItems),
                normalizePricing(currentSteps[CREATE_BUNDLE_STEP_KEYS.PRICING]?.pricingItems)
            );
        case 2:
            return !isEqual(
                normalizeRules(originalSteps[CREATE_BUNDLE_STEP_KEYS.RULES]?.rulesList),
                normalizeRules(currentSteps[CREATE_BUNDLE_STEP_KEYS.RULES]?.rulesList)
            );
        default:
            return false;
    }
};

export const hasAnyBundleStepChanges = (originalSteps, currentSteps) => {
    if (!originalSteps) return false;
    return isBundleStepModified(originalSteps, currentSteps, 0)
        || isBundleStepModified(originalSteps, currentSteps, 1)
        || isBundleStepModified(originalSteps, currentSteps, 2);
};

export const createBundle = async ({
    stepsData,
    createMutation,
    onSuccess
}) => {
    const validationData = buildCreateBundleValidationData(stepsData);

    const pricingItems = (validationData?.pricing?.pricingItems || []).map(item => {
        const { vendorIds, priceConfig, ...bundlePricingItem } = item;
        return {
            ...bundlePricingItem,
            lotId: item.lotOption === LOT_OPTIONS.SPECIFIC_LOTS ? item.selectedLotIds : null,
            price: null,
            allLots: item.lotOption === LOT_OPTIONS.ALL_LOTS,
            allTenantLots: item.lotOption === LOT_OPTIONS.ALL_PMF_LOTS,
            charge: item.price || priceConfig?.charge || priceConfig?.price
        };
    });

    const rulesList = (validationData?.rules?.rulesList || []).map(rule => ({
        ...rule,
        lotIds: Array.isArray(rule.lotId) ? rule.lotId.flat() : (rule.lotId != null ? [rule.lotId] : []),
        activityId: 0,
        vehicleTypeIdsList: rule?.vehicleTypeIdsList?.includes(null) ? null : (rule.vehicleTypeIdsList || []),
        ruleParams: (rule.ruleParams || []).filter(param => param?.paramValue),
        calcOnFirstCheckin: rule.calcOnFirstCheckin || false
    }));

    const bundleDetails = validationData.bundleDetails;

    const result = await createMutation({
        bundleDetails: {
            name: bundleDetails?.name?.trim(),
            description: bundleDetails?.description || null,
            activityItemIds: bundleDetails?.activityItemIds || [],
            paramTypes: bundleDetails?.paramTypes || []
        },
        pricing: pricingItems,
        rules: rulesList
    });

    if (!result?.error) {
        NotificationProvider.success('Bundle created successfully');
        onSuccess();
    } else {
        NotificationProvider.error('Failed to create bundle');
    }
};

export const updateBundleSave = async ({
    stepsData,
    originalSteps,
    bundleId,
    updateMutation,
    onSuccess
}) => {
    const validationData = buildCreateBundleValidationData(stepsData);
    const bundleDetails = validationData.bundleDetails;

    const pricingItems = (validationData?.pricing?.pricingItems || []).map(item => {
        const { vendorIds, priceConfig, ...bundlePricingItem } = item;
        return {
            ...bundlePricingItem,
            bundleId: bundleId,
            clientId: item.clientId,
            lotId: item.lotOption === LOT_OPTIONS.SPECIFIC_LOTS ? item.selectedLotIds : null,
            price: null,
            allLots: item.lotOption === LOT_OPTIONS.ALL_LOTS,
            allTenantLots: item.lotOption === LOT_OPTIONS.ALL_PMF_LOTS,
            charge: item.price || priceConfig?.charge || priceConfig?.price,
            vehicleTypeIdsList: item.vehicleTypeIdsList
        };
    });

    const rulesList = (validationData?.rules?.rulesList || []).map(rule => ({
        clientId: rule.clientId,
        lotIds: Array.isArray(rule.lotId) ? rule.lotId.flat() : (rule.lotId != null ? [rule.lotId] : []),
        activityId: 0,
        bundleId: bundleId,
        vehicleTypeIdsList: rule.vehicleTypeIdsList?.includes(null) ? null : (rule.vehicleTypeIdsList || []),
        calcOnCheckin: rule.calcOnCheckin || false,
        ruleOperand: rule.ruleOperand,
        daysToComplete: rule.daysToComplete,
        preferredVendorId: rule.preferredVendorId,
        ruleParams: (rule.ruleParams || []).filter((param) => param?.paramValue),
        allLots: rule.allLots || false,
        allTenantLots: rule.allTenantLots || false,
        calcOnFirstCheckin: rule.calcOnFirstCheckin || false,
        timeFrameType: rule.timeFrameType,
    }));

    const pricingChanged = isBundleStepModified(originalSteps, stepsData, 1);
    const rulesChanged = isBundleStepModified(originalSteps, stepsData, 2);

    const result = await updateMutation({
        bundleDetails: {
            id: bundleId,
            name: bundleDetails?.name?.trim(),
            description: bundleDetails?.description || null,
            activityItemIds: bundleDetails?.activityItemIds || [],
            paramTypes: bundleDetails?.paramTypes || []
        },
        pricing: pricingItems,
        rules: rulesList,
        pricingChanged,
        rulesChanged
    });

    if (!result?.error) {
        NotificationProvider.success('Bundle updated successfully');
        onSuccess();
    } else {
        NotificationProvider.error('Failed to update bundle');
    }
};
